import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  AfterViewInit,
  HostListener,
  OnDestroy,
  Inject,
  PLATFORM_ID
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Face, Pose, Hand } from 'kalidokit';
import * as CANNON from 'cannon-es';
import { SkinnedMesh } from 'three';
import { Holistic, POSE_CONNECTIONS, HAND_CONNECTIONS, FACEMESH_TESSELATION, Results } from '@mediapipe/holistic';

// ===== INTERFACES ET TYPES POUR LA SÉCURITÉ =====
interface BoneTransform {
  position: THREE.Vector3;
  rotation: THREE.Euler;
  scale: THREE.Vector3;
}

interface DebugStats {
  totalBonesDetected: number;
  totalBonesMapped: number;
  bonesByCategory: { [key: string]: number };
  missingCriticalBones: string[];
  loadingTime: number;
  lastUpdateTime: number;
  fps: number;
  errors: string[];
  warnings: string[];
}

interface PerformanceMonitor {
  frameCount: number;
  lastTime: number;
  fps: number;
  updateTime: number;
  skeletonUpdateTime: number;
}

@Component({
  selector: 'app-avatar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: `avatar.component.html`,
  styleUrl: 'avatar.component.css'
})
export class AvatarComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('container', { static: true }) containerRef!: ElementRef;
  private faceRig: any = null;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private model!: THREE.Group;
  private bones: Record<string, THREE.Bone> = {}; // Stockage des os identifiés
  private videoEl!: HTMLVideoElement;
  private holistic: any;
  private poseRig: any = null;
  private leftHandRig: any = null;
  private rightHandRig: any = null;
  private controls!: OrbitControls;
  // Ajoutez ces propriétés à la classe
  private isPageVisible: boolean = true;
  private lastRenderTime: number = 0;

  // Cartographie des os spécifiques pour le visage
  private headBones = {
    head: 'CC_Base_Head',
    neck1: 'CC_Base_NeckTwist01',
    neck2: 'CC_Base_NeckTwist02',
    jaw: 'CC_Base_JawRoot',
    tongue1: 'CC_Base_Tongue01',
    tongue2: 'CC_Base_Tongue02',
    tongue3: 'CC_Base_Tongue03',
    upperJaw: 'CC_Base_UpperJaw',
    lowerTeeth: 'CC_Base_Teeth02',
    upperTeeth: 'CC_Base_Teeth01',
    leftEye: 'CC_Base_L_Eye',
    rightEye: 'CC_Base_R_Eye',
    facialRoot: 'CC_Base_FacialBone'
  };

  private leftHandBones = {
    clavicle: 'CC_Base_L_Clavicle',
    upperArm: 'CC_Base_L_Upperarm',
    upperArmTwist1: 'CC_Base_L_UpperarmTwist01',
    upperArmTwist2: 'CC_Base_L_UpperarmTwist02',
    forearm: 'CC_Base_L_Forearm',
    forearmTwist1: 'CC_Base_L_ForearmTwist01',
    forearmTwist2: 'CC_Base_L_ForearmTwist02',
    elbow: 'CC_Base_L_ElbowShareBone',
    hand: 'CC_Base_L_Hand',
    thumb1: 'CC_Base_L_Thumb1',
    thumb2: 'CC_Base_L_Thumb2',
    thumb3: 'CC_Base_L_Thumb3',
    index1: 'CC_Base_L_Index1',
    index2: 'CC_Base_L_Index2',
    index3: 'CC_Base_L_Index3',
    mid1: 'CC_Base_L_Mid1',
    mid2: 'CC_Base_L_Mid2',
    mid3: 'CC_Base_L_Mid3',
    ring1: 'CC_Base_L_Ring1',
    ring2: 'CC_Base_L_Ring2',
    ring3: 'CC_Base_L_Ring3',
    pinky1: 'CC_Base_L_Pinky1',
    pinky2: 'CC_Base_L_Pinky2',
    pinky3: 'CC_Base_L_Pinky3'
  };

  private rightHandBones = {
    clavicle: 'CC_Base_R_Clavicle',
    upperArm: 'CC_Base_R_Upperarm',
    upperArmTwist1: 'CC_Base_R_UpperarmTwist01',
    upperArmTwist2: 'CC_Base_R_UpperarmTwist02',
    forearm: 'CC_Base_R_Forearm',
    forearmTwist1: 'CC_Base_R_ForearmTwist01',
    forearmTwist2: 'CC_Base_R_ForearmTwist02',
    elbow: 'CC_Base_R_ElbowShareBone',
    hand: 'CC_Base_R_Hand',
    thumb1: 'CC_Base_R_Thumb1',
    thumb2: 'CC_Base_R_Thumb2',
    thumb3: 'CC_Base_R_Thumb3',
    index1: 'CC_Base_R_Index1',
    index2: 'CC_Base_R_Index2',
    index3: 'CC_Base_R_Index3',
    mid1: 'CC_Base_R_Mid1',
    mid2: 'CC_Base_R_Mid2',
    mid3: 'CC_Base_R_Mid3',
    ring1: 'CC_Base_R_Ring1',
    ring2: 'CC_Base_R_Ring2',
    ring3: 'CC_Base_R_Ring3',
    pinky1: 'CC_Base_R_Pinky1',
    pinky2: 'CC_Base_R_Pinky2',
    pinky3: 'CC_Base_R_Pinky3'
  };

  private bodyBones = {
    root: 'CC_Base_BoneRoot',
    hip: 'CC_Base_Hip',
    pelvis: 'CC_Base_Pelvis',
    waist: 'CC_Base_Waist',
    spine01: 'CC_Base_Spine01',
    spine02: 'CC_Base_Spine02',
    leftThigh: 'CC_Base_L_Thigh',
    leftThighTwist1: 'CC_Base_L_ThighTwist01',
    leftThighTwist2: 'CC_Base_L_ThighTwist02',
    leftCalf: 'CC_Base_L_Calf',
    leftKnee: 'CC_Base_L_KneeShareBone',
    leftCalfTwist1: 'CC_Base_L_CalfTwist01',
    leftCalfTwist2: 'CC_Base_L_CalfTwist02',
    leftFoot: 'CC_Base_L_Foot',
    leftToeBase: 'CC_Base_L_ToeBase',
    leftToeShare: 'CC_Base_L_ToeBaseShareBone',
    rightThigh: 'CC_Base_R_Thigh',
    rightThighTwist1: 'CC_Base_R_ThighTwist01',
    rightThighTwist2: 'CC_Base_R_ThighTwist02',
    rightCalf: 'CC_Base_R_Calf',
    rightKnee: 'CC_Base_R_KneeShareBone',
    rightCalfTwist1: 'CC_Base_R_CalfTwist01',
    rightCalfTwist2: 'CC_Base_R_CalfTwist02',
    rightFoot: 'CC_Base_R_Foot',
    rightToeBase: 'CC_Base_R_ToeBase',
    rightToeShare: 'CC_Base_R_ToeBaseShareBone'
  };

  constructor(@Inject(PLATFORM_ID) private platformId: Object) { }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Ajouter écouteur d'événement pour la visibilité de page
      document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

      this.initThreeJS();
      this.startVideo();
      this.loadAvatar();
      this.animate();
    }
  }

  private handleVisibilityChange() {
    this.isPageVisible = document.visibilityState === 'visible';
    if (this.isPageVisible) {
      // Réinitialiser le temps de rendu pour éviter un grand delta
      this.lastRenderTime = performance.now();
    }
  }

  private hasValidLandmarks(landmarks: any[], minRatio: number = 0.5): boolean {
    if (!landmarks || landmarks.length === 0) {
      console.log('❌ Pas de landmarks ou array vide');
      return false;
    }

    const valid = landmarks.filter(lm => {
      // Vérification de la visibilité plus permissive
      const hasVisibility = lm.visibility !== undefined ? lm.visibility > 0.3 : true;

      // Vérification des coordonnées stricte
      const hasValidCoords =
        typeof lm.x === 'number' && !isNaN(lm.x) && isFinite(lm.x) &&
        typeof lm.y === 'number' && !isNaN(lm.y) && isFinite(lm.y) &&
        typeof lm.z === 'number' && !isNaN(lm.z) && isFinite(lm.z);

      return hasVisibility && hasValidCoords;
    });

    const validRatio = valid.length / landmarks.length;
    
    return validRatio >= minRatio;
  }

  private startVideoProcessing(videoEl: HTMLVideoElement): void {
    const processFrame = async () => {
      if (videoEl.readyState >= 2) {
        console.log('[DEBUG] Envoi frame à Holistic');
        await this.holistic.send({ image: videoEl });
      }
      requestAnimationFrame(processFrame);
    };
    processFrame();
  }

  private loadAvatar(): void {
    const loader = new GLTFLoader();
    loader.load('lara.glb', (gltf) => {
      this.model = gltf.scene;
      this.model.traverse((child) => {
        if (child instanceof THREE.Bone) {
          this.bones[child.name] = child;
          // Choisir un mode unique : auto-update ou manuel
          child.matrixAutoUpdate = true;                        // ou false si mise à jour manuelle  
          child.matrixWorldNeedsUpdate = true;                  // forcer la mise à jour initiale
        }
        if (child instanceof THREE.SkinnedMesh) {
          child.frustumCulled = false;                          // désactiver le frustum culling
          (child.material as any).skinning = true;              // activer le skinning
        }
      });
      this.scene.add(this.model);
    });
  }


  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      // 1. Récupération et stockage de la balise
      this.videoEl = this.containerRef.nativeElement.querySelector('video') as HTMLVideoElement;
      // 2. Démarrage de la caméra
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          this.videoEl.srcObject = stream;
          this.videoEl.onloadedmetadata = () => {
            // 3. Instanciation du pipeline Holistic
            this.holistic = new Holistic({
              locateFile: file => `https://cdn.jsdelivr.net/npm/@mediapipe/holistic/${file}`
            });
            this.holistic.setOptions({
              modelComplexity: 1,
              smoothLandmarks: true,
              refineFaceLandmarks: true,
              minDetectionConfidence: 0.3,
              minTrackingConfidence: 0.3
            });
            // 4. Connexion du callback de tracking
            this.holistic.onResults((results: Results) => this.processFaceTracking(results));
            // 5. Boucle d'analyse vidéo
            this.startVideoProcessing(this.videoEl);
          };
        })
        .catch(err => console.error('Erreur accès webcam :', err));
    }
  }

  private startVideo() {
    if (isPlatformBrowser(this.platformId)) {
      const videoEl = this.containerRef.nativeElement.querySelector('video') as HTMLVideoElement;
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices.getUserMedia({ video: true })
          .then(stream => videoEl.srcObject = stream)
          .catch(err => console.error('Erreur webcam :', err));
      } else {
        console.warn('Webcam non supportée');
      }
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

      if (this.controls) {
        this.controls.dispose();
      }

      // Arrêter Holistic avant destruction
      if (this.holistic) {
        this.holistic.close();
      }

      // Arrêter le flux vidéo
      if (this.videoEl && this.videoEl.srcObject) {
        const stream = this.videoEl.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }

      if (this.renderer) {
        this.renderer.dispose();
      }

      // Nettoyer toutes les géométries et matériaux
      if (this.model) {
        this.model.traverse(child => {
          if (child instanceof THREE.Mesh) {
            if (child.geometry) child.geometry.dispose();
            if (child instanceof THREE.SkinnedMesh) {
              child.geometry.computeBoundingBox();
              child.geometry.computeBoundingSphere();
            }
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(material => material.dispose());
              } else {
                child.material.dispose();
              }
            }
          }
        });
      }
    }
  }

  private processFaceTracking(results: Results): void {
    console.log('[DEBUG] processFaceTracking appelée', results);
    this.validateBoneTransformations();                         // corrige toute corruption avant application

    // 1. Visage (seuil 90%)
    if (results.faceLandmarks && this.hasValidLandmarks(results.faceLandmarks, 0.9)) {
      this.faceRig = Face.solve(results.faceLandmarks, { runtime: 'mediapipe', video: this.videoEl });
      this.applyHeadRotation(this.faceRig);
    }

    // 2. Corps (validation critiques)
    if (results.poseLandmarks && this.hasValidCorePoseLandmarks(results.poseLandmarks)) {
      this.poseRig = Pose.solve(
        results.poseLandmarks,
        results.poseLandmarks,
        { runtime: 'mediapipe', video: this.videoEl, enableLegs: true }
      );
      const poseVisibility = this.calculatePoseVisibility(results.poseLandmarks);
      this.applyPoseRotation(this.poseRig, poseVisibility);
    }

    // 3. Mains (seuil 85%)
    if (results.leftHandLandmarks && this.hasValidLandmarks(results.leftHandLandmarks, 0.85)) {
      this.leftHandRig = Hand.solve(results.leftHandLandmarks, 'Left');
      this.applyHandRotation(this.leftHandRig, 'left');
    }
    if (results.rightHandLandmarks && this.hasValidLandmarks(results.rightHandLandmarks, 0.85)) {
      this.rightHandRig = Hand.solve(results.rightHandLandmarks, 'Right');
      console.log('[DEBUG] rightHandRig:', this.rightHandRig); // ← Ajoute ce log

      this.applyHandRotation(this.rightHandRig, 'right');

      const poseVisibility = this.calculatePoseVisibility(results.poseLandmarks);

      this.applyPoseRotation(this.poseRig, poseVisibility)
    }
  }


  private validatePoseWithFallback(landmarks: any[]): boolean {
    // Validation standard
    if (this.hasValidCorePoseLandmarks(landmarks)) {
      return true;
    }

    // Fallback : vérifier au moins les épaules
    const shoulders = [11, 12]; // Épaules gauche et droite
    const validShoulders = shoulders.filter(index => {
      const lm = landmarks[index];
      return lm && !isNaN(lm.x) && !isNaN(lm.y) && !isNaN(lm.z);
    });

    return validShoulders.length >= 2;
  }


  private hasValidCorePoseLandmarks(landmarks: any[]): boolean {
    if (!landmarks || landmarks.length !== 33) return false;

    // Landmarks essentiels (indices MediaPipe)
    const essentialIndices = [11, 12, 23, 24]; // Épaules et hanches

    const validEssential = essentialIndices.filter(index => {
      const lm = landmarks[index];
      return lm && !isNaN(lm.x) && !isNaN(lm.y) && !isNaN(lm.z) &&
        isFinite(lm.x) && isFinite(lm.y) && isFinite(lm.z) &&
        (lm.visibility === undefined || lm.visibility > 0.2);
    });

    return validEssential.length >= 3; // Au moins 3 landmarks essentiels
  }

  private isValidPoseRig(rig: any): boolean {
    if (!rig) return false;

    // Vérification des propriétés essentielles
    const essentialProperties = ['LeftUpperArm', 'RightUpperArm', 'Spine'];
    return essentialProperties.some(prop =>
      rig[prop] && this.isValidRotation(rig[prop])
    );
  }

  private isValidHandRig(rig: any): boolean {
    if (!rig) return false;

    // Vérification qu'au moins 3 doigts ont des données valides
    const fingers = ['Thumb', 'Index', 'Middle', 'Ring', 'Pinky'];
    const validFingers = fingers.filter(finger =>
      rig[finger] && Array.isArray(rig[finger]) && rig[finger].length > 0
    );

    return validFingers.length >= 3;
  }

  private isValidFaceRig(rig: any): boolean {
    return rig && rig.head &&
      this.isValidRotation(rig.head) &&
      !isNaN(rig.head.x) && !isNaN(rig.head.y) && !isNaN(rig.head.z);
  }

  private applyPoseRotation(pose: any, poseVisibility: Record<string, number>): void {
    if (!pose || !this.bones) return;
    const lerp = THREE.MathUtils.lerp;
    const f = 0.3; // Facteur de lissage pour la fluidité
    const visibilityThreshold = 0.4; // Seuil de visibilité pour appliquer la rotation

    // -------- HANCHES (HIPS) --------
    {
      const b = this.bones['CC_Base_Hip'];
      const vis = poseVisibility['Hips'] ?? 0;
      if (pose.Hips && this.isValidRotation(pose.Hips) && vis >= visibilityThreshold) {
        b.rotation.set(
          lerp(b.rotation.x, -pose.Hips.x, f),
          lerp(b.rotation.y, -pose.Hips.y, f),
          lerp(b.rotation.z, pose.Hips.z, f)
        );
      } else if (b) {
        // Ramener à la pose neutre si invisible
        b.rotation.set(
          lerp(b.rotation.x, 0, f * 0.2),
          lerp(b.rotation.y, 0, f * 0.2),
          lerp(b.rotation.z, 0, f * 0.2)
        );
      }
      if (b) {
        b.updateMatrix();
        b.updateMatrixWorld(true);
      }
    }

    // -------- COLONNE (SPINE) --------
    const spineBones = ['CC_Base_Spine01', 'CC_Base_Spine02'];
    spineBones.forEach((name, idx) => {
      const b = this.bones[name];
      const vis = poseVisibility['Spine'] ?? 0;
      if (pose.Spine && this.isValidRotation(pose.Spine) && vis >= visibilityThreshold) {
        const scale = idx === 0 ? 1.0 : 0.5;
        b.rotation.set(
          lerp(b.rotation.x, -pose.Spine.x * scale, f),
          lerp(b.rotation.y, -pose.Spine.y * scale, f),
          lerp(b.rotation.z, pose.Spine.z * scale, f)
        );
      } else if (b) {
        b.rotation.set(
          lerp(b.rotation.x, 0, f * 0.2),
          lerp(b.rotation.y, 0, f * 0.2),
          lerp(b.rotation.z, 0, f * 0.2)
        );
      }
      if (b) {
        b.updateMatrix();
        b.updateMatrixWorld(true);
      }
    });

    // -------- ABDOMEN (WAIST) --------
    {
      const abdomen = this.bones['CC_Base_Waist'];
      const vis = poseVisibility['Spine'] ?? 0;
      if (pose.Spine && this.isValidRotation(pose.Spine) && vis >= visibilityThreshold) {
        abdomen.rotation.set(
          lerp(abdomen.rotation.x, -pose.Spine.x * 0.7, f),
          lerp(abdomen.rotation.y, -pose.Spine.y * 0.7, f),
          lerp(abdomen.rotation.z, pose.Spine.z * 0.7, f)
        );
      } else if (abdomen) {
        abdomen.rotation.set(
          lerp(abdomen.rotation.x, 0, f * 0.2),
          lerp(abdomen.rotation.y, 0, f * 0.2),
          lerp(abdomen.rotation.z, 0, f * 0.2)
        );
      }
      if (abdomen) {
        abdomen.updateMatrix();
        abdomen.updateMatrixWorld(true);
      }
    }

    // -------- BRAS --------
    const armMappings = [
      { prop: 'LeftUpperArm', bone: 'CC_Base_L_Upperarm' },
      { prop: 'RightUpperArm', bone: 'CC_Base_R_Upperarm' },
      { prop: 'LeftLowerArm', bone: 'CC_Base_L_Forearm' },
      { prop: 'RightLowerArm', bone: 'CC_Base_R_Forearm' }
    ];
    armMappings.forEach(({ prop, bone }) => {
      const b = this.bones[bone];
      const vis = poseVisibility[prop] ?? 0;
      if (pose[prop] && this.isValidRotation(pose[prop]) && vis >= visibilityThreshold) {
        b.rotation.set(
          lerp(b.rotation.x, -pose[prop].x, f),
          lerp(b.rotation.y, -pose[prop].y, f),
          lerp(b.rotation.z, pose[prop].z, f)
        );
      } else if (b) {
        b.rotation.set(
          lerp(b.rotation.x, 0, f * 0.2),
          lerp(b.rotation.y, 0, f * 0.2),
          lerp(b.rotation.z, 0, f * 0.2)
        );
      }
      if (b) {
        b.updateMatrix();
        b.updateMatrixWorld(true);
      }
    });

    // -------- JAMBES --------
    const legMappings = [
      { prop: 'LeftUpperLeg', bone: 'CC_Base_L_Thigh' },
      { prop: 'RightUpperLeg', bone: 'CC_Base_R_Thigh' },
      { prop: 'LeftLowerLeg', bone: 'CC_Base_L_Calf' },
      { prop: 'RightLowerLeg', bone: 'CC_Base_R_Calf' }
    ];
    legMappings.forEach(({ prop, bone }) => {
      const b = this.bones[bone];
      const vis = poseVisibility[prop] ?? 0;
      if (pose[prop] && this.isValidRotation(pose[prop]) && vis >= visibilityThreshold) {
        b.rotation.set(
          lerp(b.rotation.x, -pose[prop].x, f),
          lerp(b.rotation.y, -pose[prop].y, f),
          lerp(b.rotation.z, pose[prop].z, f)
        );
      } else if (b) {
        b.rotation.set(
          lerp(b.rotation.x, 0, f * 0.2),
          lerp(b.rotation.y, 0, f * 0.2),
          lerp(b.rotation.z, 0, f * 0.2)
        );
      }
      if (b) {
        b.updateMatrix();
        b.updateMatrixWorld(true);
      }
    });

    // -------- MAINS --------
    if (pose.LeftHand && this.bones['CC_Base_L_Hand']) {
      this.applyHandRotation(pose.LeftHand, 'left');
    }
    if (pose.RightHand && this.bones['CC_Base_R_Hand']) {
      this.applyHandRotation(pose.RightHand, 'right');
    }
  }


  private applyHandRotation(hand: any, side: 'left' | 'right'): void {
    if (!hand) return;
    const lerp = THREE.MathUtils.lerp, f = 0.4;

    // Mapping Kalidokit → bones
    const kalidokitToBone = side === 'right'
      ? {
        RightWrist: 'CC_Base_R_Hand',
        RightThumbProximal: 'CC_Base_R_Thumb1',
        RightThumbIntermediate: 'CC_Base_R_Thumb2',
        RightThumbDistal: 'CC_Base_R_Thumb3',
        RightIndexProximal: 'CC_Base_R_Index1',
        RightIndexIntermediate: 'CC_Base_R_Index2',
        RightIndexDistal: 'CC_Base_R_Index3',
        RightMiddleProximal: 'CC_Base_R_Mid1',
        RightMiddleIntermediate: 'CC_Base_R_Mid2',
        RightMiddleDistal: 'CC_Base_R_Mid3',
        RightRingProximal: 'CC_Base_R_Ring1',
        RightRingIntermediate: 'CC_Base_R_Ring2',
        RightRingDistal: 'CC_Base_R_Ring3',
        RightPinkyProximal: 'CC_Base_R_Pinky1',
        RightPinkyIntermediate: 'CC_Base_R_Pinky2',
        RightPinkyDistal: 'CC_Base_R_Pinky3'
      }
      : {
        LeftWrist: 'CC_Base_L_Hand',
        LeftThumbProximal: 'CC_Base_L_Thumb1',
        LeftThumbIntermediate: 'CC_Base_L_Thumb2',
        LeftThumbDistal: 'CC_Base_L_Thumb3',
        LeftIndexProximal: 'CC_Base_L_Index1',
        LeftIndexIntermediate: 'CC_Base_L_Index2',
        LeftIndexDistal: 'CC_Base_L_Index3',
        LeftMiddleProximal: 'CC_Base_L_Mid1',
        LeftMiddleIntermediate: 'CC_Base_L_Mid2',
        LeftMiddleDistal: 'CC_Base_L_Mid3',
        LeftRingProximal: 'CC_Base_L_Ring1',
        LeftRingIntermediate: 'CC_Base_L_Ring2',
        LeftRingDistal: 'CC_Base_L_Ring3',
        LeftPinkyProximal: 'CC_Base_L_Pinky1',
        LeftPinkyIntermediate: 'CC_Base_L_Pinky2',
        LeftPinkyDistal: 'CC_Base_L_Pinky3'
      };

    Object.entries(kalidokitToBone).forEach(([kaliKey, boneName]) => {
      const rot = hand[kaliKey];
      const bone = this.bones[boneName];
      if (!rot || !bone) return;
      if (!this.isValidRotation(rot)) return;
      bone.rotation.set(
        lerp(bone.rotation.x, rot.x, f),
        lerp(bone.rotation.y, rot.y, f),
        lerp(bone.rotation.z, rot.z, f)
      );
      bone.updateMatrix();
      bone.updateMatrixWorld(true);
    });
  }

  private calculatePoseVisibility(landmarks: any[]): Record<string, number> {
    const visibility: Record<string, number> = {};
    visibility['Hips'] = (landmarks[23].visibility + landmarks[24].visibility) / 2;
    visibility['Spine'] = (
      landmarks[11].visibility +
      landmarks[12].visibility +
      landmarks[23].visibility +
      landmarks[24].visibility
    ) / 4;
    visibility['LeftUpperArm'] = (
      landmarks[11].visibility +
      landmarks[13].visibility
    ) / 2;
    visibility['RightUpperArm'] = (
      landmarks[12].visibility +
      landmarks[14].visibility
    ) / 2;
    visibility['LeftUpperLeg'] = (landmarks[23].visibility + landmarks[25].visibility) / 2;
    visibility['RightUpperLeg'] = (landmarks[24].visibility + landmarks[26].visibility) / 2;
    return visibility;
  }


  private isValidRotation(rotation: any): boolean {
    return rotation &&
      typeof rotation.x === 'number' && isFinite(rotation.x) &&
      typeof rotation.y === 'number' && isFinite(rotation.y) &&
      typeof rotation.z === 'number' && isFinite(rotation.z);
  }


  private applyHeadRotation(face: any): void {
    if (!face || !this.bones) return;

    const lerp = THREE.MathUtils.lerp;
    const lerpFactor = 0.5;

    // Contrôle de la tête (rotation)
    if (this.bones['CC_Base_Head'] && face.head) {
      const head = this.bones['CC_Base_Head'];
      head.rotation.x = lerp(head.rotation.x, -face.head.y, lerpFactor); // pitch
      head.rotation.y = lerp(head.rotation.y, -face.head.x, lerpFactor); // yaw
      head.rotation.z = lerp(head.rotation.z, face.head.z, lerpFactor); // roll
    }

    // Contrôle du cou
    if (this.bones['CC_Base_NeckTwist01']) {
      const neck = this.bones['CC_Base_NeckTwist01'];
      neck.rotation.x = lerp(neck.rotation.x, -face.head.y * 0.3, lerpFactor);
      neck.rotation.y = lerp(neck.rotation.y, -face.head.x * 0.3, lerpFactor);
      neck.rotation.z = lerp(neck.rotation.z, face.head.z * 0.3, lerpFactor);
    }

    // Optionnel : yeux (si tu veux les suivre aussi)
    if (this.bones['CC_Base_L_Eye']) {
      this.bones['CC_Base_L_Eye'].rotation.y = lerp(this.bones['CC_Base_L_Eye'].rotation.y, face.eye.l, lerpFactor);
    }

    if (this.bones['CC_Base_R_Eye']) {
      this.bones['CC_Base_R_Eye'].rotation.y = lerp(this.bones['CC_Base_R_Eye'].rotation.y, face.eye.r, lerpFactor);
    }

  }



  /** Initialisation scène, camera, lumière et sol */
  private initThreeJS() {
    // Scène
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf0f0f0);

    // Caméra
    const { clientWidth: w, clientHeight: h } = this.containerRef.nativeElement;
    this.camera = new THREE.PerspectiveCamera(75, w / h, 0.1, 1000);
    this.camera.position.set(0, 1.6, 3);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(w, h);
    this.renderer.shadowMap.enabled = true;
    this.containerRef.nativeElement.appendChild(this.renderer.domElement);

    // Lumières
    this.scene.add(new THREE.AmbientLight(0x404040, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(5, 10, 5);
    dirLight.castShadow = true;
    this.scene.add(dirLight);

    // Sol
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(10, 10),
      new THREE.MeshLambertMaterial({ color: 0xcccccc })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.scene.add(floor);

    // OrbitControls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.enableZoom = true;
    this.controls.enablePan = true;
    this.controls.maxDistance = 10;
    this.controls.minDistance = 1;
    this.controls.update(); // Important
  }

  private simulateTestRotations(): void {
  // Cou
  const neck = this.bones['CC_Base_NeckTwist01'];
  if (neck) {
    neck.rotation.y += 0.05;
    neck.updateMatrix(); neck.updateMatrixWorld(true);
  }
  // Bras droit
  const upperArm = this.bones['CC_Base_R_Upperarm'];
  if (upperArm) {
    upperArm.rotation.z += 0.05;
    upperArm.updateMatrix(); upperArm.updateMatrixWorld(true);
  }
  // Main droite
  const hand = this.bones['CC_Base_R_Hand'];
  if (hand) {
    hand.rotation.x += 0.1;
    hand.updateMatrix(); hand.updateMatrixWorld(true);
  }
}

  private validateBoneTransformations(): void {
    this.validateHipBone();                                     // priorité au bone hip

    Object.entries(this.bones).forEach(([name, bone]) => {
      if (!bone) return;
      const hasNaNRot = [bone.rotation.x, bone.rotation.y, bone.rotation.z]
        .some(v => isNaN(v));
      const hasNaNPos = [bone.position.x, bone.position.y, bone.position.z]
        .some(v => isNaN(v));
      const hasExtRot = [bone.rotation.x, bone.rotation.y, bone.rotation.z]
        .some(v => Math.abs(v) > Math.PI * 2);
      const hasExtPos = [bone.position.x, bone.position.y, bone.position.z]
        .some(v => Math.abs(v) > 50);

      if (hasNaNRot || hasNaNPos || hasExtRot || hasExtPos) {
        console.warn(`🔧 Correction bone corrompu: ${name}`);
        bone.scale.set(1, 1, 1);
        bone.matrix.identity();
        bone.matrixWorld.identity();
        bone.updateMatrix();
        bone.updateMatrixWorld(true);
      }
    });
  }

  private validateHipBone(): void {
    const hip = this.bones['CC_Base_Hip'];
    if (!hip) return;
    const invRot = [hip.rotation.x, hip.rotation.y, hip.rotation.z]
      .some(v => isNaN(v) || Math.abs(v) > Math.PI);
    const invPos = isNaN(hip.position.y) || Math.abs(hip.position.y) > 3;
    if (invRot || invPos) {
      console.warn('🚨 Reset critique du Hip bone détecté');
      hip.matrix.identity();
      hip.updateMatrix();
      hip.updateMatrixWorld(true);
      this.emergencyResetAvatar();                               // reset complet si persistance
    }
  }

  private validateCriticalBoneHierarchy(bone: THREE.Bone): void {
    // Validation et correction des bones enfants dans la hiérarchie
    bone.traverse((child) => {
      if (child instanceof THREE.Bone) {
        if (isNaN(child.rotation.x) || isNaN(child.rotation.y) || isNaN(child.rotation.z)) {
          child.rotation.set(0, 0, 0);
          child.updateMatrix();
          child.updateMatrixWorld(true);
        }
      }
    });
  }

  private emergencyResetAvatar(): void {
    console.log('🚨 Reset d\'urgence complet de l\'avatar');

    Object.entries(this.bones).forEach(([name, bone]) => {
      if (bone) {
        bone.scale.set(1, 1, 1);
        bone.matrix.identity();
        bone.matrixWorld.identity();
        bone.updateMatrix();
        bone.updateMatrixWorld(true);
      }
    });

    // Validation post-reset
    setTimeout(() => {
      this.validateBoneTransformations();
    }, 100);
  }


  /** Boucle d'animation de la scène et de la caméra */
  private animate() {
    if (!isPlatformBrowser(this.platformId)) return;

    // Surveillance critique du Hip bone avant rendu
    const hip = this.bones['CC_Base_Hip'];
    if (hip) {
      if (hip.position.y < -1.5) { // Seuil ajusté pour détection précoce
        console.warn('🚨 Avatar en chute détecté - Reset immédiat');
        this.emergencyResetAvatar();
      }

      // Vérification des rotations extrêmes
      if (Math.abs(hip.rotation.x) > Math.PI || Math.abs(hip.rotation.y) > Math.PI || Math.abs(hip.rotation.z) > Math.PI) {
        console.warn('🚨 Rotation Hip extrême détectée - Correction');
        hip.rotation.set(0, 0, 0);
        hip.updateMatrix();
        hip.updateMatrixWorld(true);
      }
    }

    // Validation périodique des transformations
    this.validateBoneTransformations(); //error initilise here

    // Mise à jour des contrôles et rendu
    if (this.controls) {
      this.controls.update();
    }

    requestAnimationFrame(() => this.animate());
    this.renderer.render(this.scene, this.camera);
  }

  @HostListener('window:keydown', ['$event'])
  private handleKey(event: KeyboardEvent) {
    const s = 0.1;
    switch (event.key.toLowerCase()) {
      case 'q': this.rotateBone('CC_Base_Head', 'y', -s); break; // Tête
      case 'a': this.rotateBone('CC_Base_Head', 'y', s); break; // Tête
      case 'w': this.rotateBone('CC_Base_NeckTwist01', 'y', -s); break;// Cou
      case 's': this.rotateBone('CC_Base_NeckTwist01', 'y', s); break; // Cou
      case 'e': this.rotateBone('CC_Base_Spine02', 'y', -s); break; // Torse
      case 'd': this.rotateBone('CC_Base_Spine02', 'y', s); break; // Torse
      case 'r': this.rotateBone('CC_Base_L_Upperarm', 'z', -s); break; // Bras G élév
      case 't': this.rotateBone('CC_Base_R_Upperarm', 'z', -s); break; // Bras D élév
      case 'f': this.rotateBone('CC_Base_L_Upperarm', 'z', s); break; // Bras G élév
      case 'g': this.rotateBone('CC_Base_R_Upperarm', 'z', s); break; // Bras D élév
      case 'y': this.rotateBone('CC_Base_L_Forearm', 'z', -s); break; // Coude G
      case 'u': this.rotateBone('CC_Base_R_Forearm', 'z', -s); break; // Coude D
      case 'h': this.rotateBone('CC_Base_L_Forearm', 'z', s); break; // Coude G
      case 'j': this.rotateBone('CC_Base_R_Forearm', 'z', s); break; // Coude D
      case 'i': this.rotateBone('CC_Base_L_Thigh', 'x', -s); break; // Hanche G
      case 'o': this.rotateBone('CC_Base_R_Thigh', 'x', -s); break; // Hanche D
      case 'k': this.rotateBone('CC_Base_L_Thigh', 'x', s); break; // Hanche G
      case 'l': this.rotateBone('CC_Base_R_Thigh', 'x', s); break; // Hanche D
      case 'p': this.rotateBone('CC_Base_L_Calf', 'x', -s); break; // Genou G
      case '[': this.rotateBone('CC_Base_R_Calf', 'x', -s); break; // Genou D
      case ';': this.rotateBone('CC_Base_L_Calf', 'x', s); break; // Genou G
      case ']': this.rotateBone('CC_Base_R_Calf', 'x', s); break; // Genou D
      // Doigts main gauche
      case '1': this.rotateBone('CC_Base_L_Index1', 'z', -s); break; // Index G
      case '2': this.rotateBone('CC_Base_L_Index1', 'z', s); break; // Index G
      case '3': this.rotateBone('CC_Base_L_Mid1', 'z', -s); break; // Majeur G
      case '4': this.rotateBone('CC_Base_L_Mid1', 'z', s); break; // Majeur G
      case '5': this.rotateBone('CC_Base_L_Ring1', 'z', -s); break; // Annulaire G
      case '6': this.rotateBone('CC_Base_L_Ring1', 'z', s); break; // Annulaire G
      case '7': this.rotateBone('CC_Base_L_Pinky1', 'z', -s); break; // Auriculaire G
      case '8': this.rotateBone('CC_Base_L_Pinky1', 'z', s); break; // Auriculaire G
      case '9': this.rotateBone('CC_Base_L_Thumb1', 'z', -s); break; // Pouce G
      case '0': this.rotateBone('CC_Base_L_Thumb1', 'z', s); break; // Pouce G
      // Doigts main droite
      case '-': this.rotateBone('CC_Base_R_Index1', 'z', -s); break; // Index D
      case '=': this.rotateBone('CC_Base_R_Index1', 'z', s); break; // Index D
      case 'z': this.rotateBone('CC_Base_R_Mid1', 'z', -s); break; // Majeur D
      case 'x': this.rotateBone('CC_Base_R_Mid1', 'z', s); break; // Majeur D
      case 'c': this.rotateBone('CC_Base_R_Ring1', 'z', -s); break; // Annulaire D
      case 'v': this.rotateBone('CC_Base_R_Ring1', 'z', s); break; // Annulaire D
      case 'b': this.rotateBone('CC_Base_R_Pinky1', 'z', -s); break; // Auriculaire D
      case 'n': this.rotateBone('CC_Base_R_Pinky1', 'z', s); break; // Auriculaire D
      case 'm': this.rotateBone('CC_Base_R_Thumb1', 'z', -s); break; // Pouce D
      case ',': this.rotateBone('CC_Base_R_Thumb1', 'z', s); break; // Pouce D
      case ' ':
        this.emergencyResetAvatar();
        event.preventDefault();
        break;

    }
    event.preventDefault();
  }

  private rotateBone(name: string, axis: 'x' | 'y' | 'z', angle: number) {
    const b = this.bones[name];
    if (b) b.rotation[axis] += angle;
    else console.warn(`Os '${name}' introuvable`);
  }

  private resetPose() {
    Object.values(this.bones).forEach(b => b.rotation.set(0, 0, 0));
  }

  /** Ajuste le rendu lors du redimensionnement de la fenêtre */
  @HostListener('window:resize')
  onWindowResize() {
    const { clientWidth: w, clientHeight: h } = this.containerRef.nativeElement;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }
}
